/**
 * /directory/au — Australia-wide directory landing.
 * Hero stat strip ("9,274 suburbs · 8 states · 24K listings") + 8 state tiles
 * with entity counts, top niches, and a CTA into the directory.
 *
 * Default view is SEQ/Brisbane per the user's spec.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DirectoryFreshStrip from "@/components/DirectoryFreshStrip";

const STATE_FLAGS = {
  ACT: "🇦🇺", NSW: "🇦🇺", NT: "🇦🇺", QLD: "🇦🇺",
  SA: "🇦🇺", TAS: "🇦🇺", VIC: "🇦🇺", WA: "🇦🇺",
};

const STATE_ACCENT = {
  QLD: "#f59e0b", NSW: "#06b6d4", VIC: "#8b5cf6",
  SA:  "#ec4899", WA:  "#10b981", TAS: "#3b82f6",
  NT:  "#ef4444", ACT: "#a855f7",
};

export default function DirectoryAUPage() {
  const [data, setData] = useState(null);
  const [hover, setHover] = useState(null);
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
    fetch(`${API}/api/directory/au/overview`).then(r => r.json()).then(setData);
  }, []);

  if (!data) {
    return <div className="dau-loading" data-testid="directory-au-loading">Loading Australia…</div>;
  }

  const sortedStates = [...data.states].sort((a, b) => b.entity_count - a.entity_count);
  const totalListings = data.totals.entities.toLocaleString();
  const totalSuburbs = data.totals.suburbs.toLocaleString();

  return (
    <div className="dau-root" data-testid="directory-au-page">
      {/* Hero */}
      <section className="dau-hero">
        <div className="dau-container">
          <div className="dau-eyebrow">HustlePlate Directory · Australia</div>
          <h1 className="dau-title">
            <span className="dau-title__num">{totalListings}</span> listings
            <span className="dau-title__sep">·</span>
            <span className="dau-title__num">{totalSuburbs}</span> suburbs
            <span className="dau-title__sep">·</span>
            <span className="dau-title__num">{data.totals.states}</span> states
          </h1>
          <p className="dau-sub">
            Every café, bottleshop, clinic, hotel and tradie — geocoded down to
            the suburb. Default view <strong>South East Queensland</strong>.
            Click any state to drill in.
          </p>
          <div className="dau-hero-ctas">
            <Link
              to="/directory?state_code=QLD"
              className="dau-btn dau-btn--primary"
              data-testid="directory-au-cta-seq"
            >
              Explore SEQ now →
            </Link>
            <Link
              to="/platform#savings-calculator"
              className="dau-btn dau-btn--ghost"
              data-testid="directory-au-cta-calc"
            >
              Calculate my savings
            </Link>
          </div>
        </div>
      </section>

      <DirectoryFreshStrip variant="dark" />

      {/* States grid */}
      <section className="dau-section">
        <div className="dau-container">
          <h2 className="dau-h2">Every state, every postcode</h2>
          <div className="dau-grid" data-testid="directory-au-state-grid">
            {sortedStates.map((s) => (
              <Link
                key={s.state_code}
                to={`/directory?state_code=${s.state_code}`}
                onMouseEnter={() => setHover(s.state_code)}
                onMouseLeave={() => setHover(null)}
                className={`dau-tile ${s.entity_count > 0 ? "dau-tile--live" : "dau-tile--soon"}`}
                style={{ "--accent": STATE_ACCENT[s.state_code] || "#64748b" }}
                data-testid={`directory-au-state-${s.state_code}`}
              >
                <div className="dau-tile__head">
                  <span className="dau-tile__flag">{STATE_FLAGS[s.state_code]}</span>
                  <span className="dau-tile__code">{s.state_code}</span>
                </div>
                <div className="dau-tile__name">{s.state_name}</div>
                <div className="dau-tile__stats">
                  <div className="dau-tile__stat">
                    <div className="dau-tile__stat-num">{s.entity_count.toLocaleString()}</div>
                    <div className="dau-tile__stat-l">listings</div>
                  </div>
                  <div className="dau-tile__stat">
                    <div className="dau-tile__stat-num">{s.suburb_count.toLocaleString()}</div>
                    <div className="dau-tile__stat-l">suburbs</div>
                  </div>
                </div>
                {s.top_niches.length > 0 ? (
                  <div className="dau-tile__niches">
                    {s.top_niches.map((n) => (
                      <span key={n.niche} className="dau-tile__niche">
                        {n.niche} <em>{n.count.toLocaleString()}</em>
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="dau-tile__coming">Coming soon</div>
                )}
                {hover === s.state_code && s.entity_count > 0 && (
                  <div className="dau-tile__cta" data-testid={`directory-au-state-${s.state_code}-cta`}>
                    Browse {s.state_code} →
                  </div>
                )}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Regions (Brisbane / Gold Coast / Sunshine Coast etc.) */}
      <section className="dau-section dau-section--alt">
        <div className="dau-container">
          <h2 className="dau-h2">South East Queensland regions</h2>
          <p className="dau-section-sub">Our flagship coverage area · 6 council regions, 8 SEQ cities</p>
          <div className="dau-regions" data-testid="directory-au-regions">
            {data.regions
              .filter(r => r.state_code === "QLD")
              .map((r) => (
                <Link
                  key={r._id}
                  to={`/directory?region=${r.region_slug}`}
                  className="dau-region"
                  data-testid={`directory-au-region-${r.region_slug}`}
                >
                  <div className="dau-region__name">{r.region_name}</div>
                  <div className="dau-region__meta">
                    {r.entity_count > 0
                      ? <span>{r.entity_count.toLocaleString()} listings</span>
                      : <span className="dau-region__meta--soon">expanding</span>}
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
}
